import type {
  EvidenceQualityStatus,
  EvidenceSource,
  MeetingEvent,
  MeetingInterpretation,
} from "@/lib/behavior/behaviorContracts";
import type { CoachLensSource, CoachTwinSource } from "@/lib/coachSessionReviewContract";

type TranscriptLike = {
  id?: string | null;
  leader_id?: string | null;
  meeting_id?: string | null;
  summary_text?: string | null;
  created_at?: string | null;
};

type PracticeSummaryLike = {
  id?: string | null;
  meeting_id?: string | null;
  transcript_summary?: string | null;
};

type SessionRecordLike = {
  id?: string | null;
};

type CoachMemoryLike = {
  recentNodes?: Array<{ id?: string | null }>;
};

function trimText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function buildEvidenceSource(params: {
  kind: EvidenceSource["kind"];
  id: string | null;
  meetingId: string | null;
  leaderId: string | null;
  createdAt: string | null;
  summaryText?: string | null;
  quality: EvidenceQualityStatus;
  present: boolean;
}): EvidenceSource {
  return {
    kind: params.kind,
    id: params.id,
    meetingId: params.meetingId,
    leaderId: params.leaderId,
    createdAt: params.createdAt,
    summaryText: trimText(params.summaryText),
    quality: params.quality,
    status: params.present ? "present" : "missing",
  };
}

export function buildMeetingEvent(params: {
  transcript?: TranscriptLike | null;
  meeting?: { id?: string | null; title?: string | null; start_time?: string | null } | null;
}): MeetingEvent {
  return {
    id: params.meeting?.id ?? params.transcript?.meeting_id ?? null,
    leaderId: params.transcript?.leader_id ?? null,
    title: params.meeting?.title ?? null,
    startedAt: params.meeting?.start_time ?? null,
    transcriptUploadId: params.transcript?.id ?? null,
  };
}

export function buildMeetingEvidenceSources(params: {
  transcript?: TranscriptLike | null;
  practiceSummary?: PracticeSummaryLike | null;
  sessionRecord?: SessionRecordLike | null;
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
  coachMemory?: CoachMemoryLike | null;
  meetingInterpretation: MeetingInterpretation;
}): EvidenceSource[] {
  const meetingId = params.transcript?.meeting_id ?? params.practiceSummary?.meeting_id ?? params.meetingInterpretation.meetingId;
  const leaderId = params.transcript?.leader_id ?? null;

  return [
    buildEvidenceSource({
      kind: "practice_summary",
      id: params.practiceSummary?.id ?? null,
      meetingId,
      leaderId,
      createdAt: null,
      summaryText: params.practiceSummary?.transcript_summary ?? null,
      quality: params.meetingInterpretation.summarySource === "practice_summary" ? "canonical" : "degraded",
      present: Boolean(params.practiceSummary?.id || params.practiceSummary?.transcript_summary),
    }),
    buildEvidenceSource({
      kind: "transcript_upload",
      id: params.transcript?.id ?? null,
      meetingId,
      leaderId,
      createdAt: params.transcript?.created_at ?? null,
      summaryText: params.transcript?.summary_text ?? null,
      quality: params.meetingInterpretation.summarySource === "transcript_upload" ? "fallback" : "degraded",
      present: Boolean(params.transcript?.id),
    }),
    buildEvidenceSource({
      kind: "coach_session_record",
      id: params.sessionRecord?.id ?? null,
      meetingId,
      leaderId,
      createdAt: null,
      quality: params.sessionRecord?.id ? "canonical" : "degraded",
      present: Boolean(params.sessionRecord?.id),
    }),
    buildEvidenceSource({
      kind: "coach_lens",
      id: null,
      meetingId,
      leaderId,
      createdAt: null,
      quality: params.coachLens ? "canonical" : "degraded",
      present: Boolean(params.coachLens),
    }),
    buildEvidenceSource({
      kind: "coach_voice_profile",
      id: null,
      meetingId,
      leaderId,
      createdAt: null,
      quality: params.coachTwin ? "canonical" : "degraded",
      present: Boolean(params.coachTwin),
    }),
    buildEvidenceSource({
      kind: "coach_memory",
      id: params.coachMemory?.recentNodes?.[0]?.id ?? null,
      meetingId,
      leaderId,
      createdAt: null,
      quality: params.coachMemory?.recentNodes?.length ? "canonical" : "degraded",
      present: Boolean(params.coachMemory?.recentNodes?.length),
    }),
  ];
}
